import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Check, CheckCircle2, Globe, LayoutGrid, Loader2, Plug, Radar, Sparkles, Store, TriangleAlert } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { ArdiOrb } from '../../../components/design-system/common/ArdiOrb';
import { WorkspaceSwatch } from '../../../modules/vendor/side-bar/WorkspaceSwitcher';
import { workspaceById } from '../../../modules/vendor/side-bar/workspaces';
import { ShopConnection, useCommerceApi } from '../../../common/hooks/use-commerce-api.hook';
import {
    GOALS,
    Goal,
    Notebook,
    PrepItem,
    SOCIAL_PLATFORMS,
    SocialPlatform,
    cleanHandle,
    toggleGoal,
} from '../../../modules/vendor/onboarding/journey';
import { BrowserFrame } from './BrowserFrame';
import { LogText, SOCIAL_ICON, platformLabel } from './ArdiNotebook';

/* ───────────────────────────── shared pieces ───────────────────────────── */

export const StepTitle: React.FC<{ eyebrow?: React.ReactNode; title: React.ReactNode; subtitle?: React.ReactNode }> = ({ eyebrow, title, subtitle }) => (
    <div>
        {eyebrow && <p className="mb-3 text-xs font-medium text-muted-foreground">{eyebrow}</p>}
        <h1 className="font-heading text-[26px] leading-tight font-medium tracking-tight text-balance sm:text-[30px]">{title}</h1>
        {subtitle && <p className="mt-2.5 max-w-[480px] text-sm leading-relaxed text-muted-foreground">{subtitle}</p>}
    </div>
);

export const StepActions: React.FC<{
    onBack?: () => void;
    onNext: () => void;
    nextLabel: React.ReactNode;
    nextDisabled?: boolean;
    busy?: boolean;
    secondary?: { label: React.ReactNode; onClick: () => void };
    testId?: string;
}> = ({ onBack, onNext, nextLabel, nextDisabled, busy, secondary, testId }) => {
    const { t } = useTranslation();
    return (
        <div className="mt-8 flex flex-wrap items-center gap-2">
            {onBack && (
                <Button type="button" variant="ghost" size="lg" onClick={onBack} className="text-muted-foreground">
                    <ArrowLeft className="rtl:rotate-180" data-icon="inline-start" />
                    {t('ONBOARDING_V2.BACK')}
                </Button>
            )}
            <Button type="button" variant="outline" size="lg" onClick={onNext} disabled={nextDisabled || busy} data-testid={testId}>
                {busy && <Spinner />}
                {nextLabel}
                {!busy && <ArrowRight className="rtl:rotate-180" data-icon="inline-end" />}
            </Button>
            {secondary && (
                <Button type="button" variant="ghost" size="lg" onClick={secondary.onClick} className="text-muted-foreground">
                    {secondary.label}
                </Button>
            )}
        </div>
    );
};

/* ─────────────────────────────── welcome ─────────────────────────────── */

export const WelcomeStep: React.FC<{ name?: string; onNext: () => void }> = ({ name, onNext }) => {
    const { t } = useTranslation();
    const promises = [
        { icon: Globe, key: 'READ' },
        { icon: Radar, key: 'MARKET' },
        { icon: LayoutGrid, key: 'PREPARE' },
    ];
    return (
        <div className="mx-auto flex max-w-[560px] flex-col items-center text-center">
            <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: 'spring', stiffness: 200, damping: 18 }}>
                <ArdiOrb size={76} />
            </motion.div>
            <motion.h1
                className="mt-8 font-heading text-[30px] leading-tight font-medium tracking-tight text-balance sm:text-[36px]"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15, duration: 0.5 }}
            >
                {name ? t('ONBOARDING_V2.WELCOME.TITLE', { name }) : t('ONBOARDING_V2.WELCOME.TITLE_NO_NAME')}
            </motion.h1>
            <motion.p
                className="mt-3 max-w-[460px] text-[15px] leading-relaxed text-muted-foreground"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.5 }}
            >
                {t('ONBOARDING_V2.WELCOME.SUBTITLE')}
            </motion.p>

            <ul className="mt-9 grid w-full gap-2.5 text-start sm:grid-cols-3">
                {promises.map((p, i) => (
                    <motion.li
                        key={p.key}
                        className="flex flex-col gap-2 rounded-xl border bg-card p-4"
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.45 + i * 0.12, duration: 0.45 }}
                    >
                        <span className="flex size-8 items-center justify-center rounded-lg border bg-background">
                            <p.icon className="size-4 text-muted-foreground" />
                        </span>
                        <span className="text-sm font-medium">{t(`ONBOARDING_V2.WELCOME.${p.key}`)}</span>
                        <span className="text-xs leading-relaxed text-muted-foreground">{t(`ONBOARDING_V2.WELCOME.${p.key}_BODY`)}</span>
                    </motion.li>
                ))}
            </ul>

            <motion.div className="mt-9 flex flex-col items-center gap-3" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9 }}>
                <Button variant="outline" size="lg" onClick={onNext} autoFocus data-testid="journey-begin">
                    {t('ONBOARDING_V2.WELCOME.START')}
                    <ArrowRight className="rtl:rotate-180" data-icon="inline-end" />
                </Button>
                <p className="text-xs text-muted-foreground">{t('ONBOARDING_V2.WELCOME.TIME')}</p>
            </motion.div>
        </div>
    );
};

/* ─────────────────────────────── website ─────────────────────────────── */

/** Deliberately loose: a vendor types "acme.com", and that is a website. */
export function looksLikeUrl(value: string): boolean {
    if (!value) return false;
    const bare = value.trim().replace(/^https?:\/\//i, '').split('/')[0];
    return /^[a-z0-9-]+(\.[a-z0-9-]+)+$/i.test(bare);
}

export const normaliseUrl = (value: string) => (/^https?:\/\//i.test(value.trim()) ? value.trim() : `https://${value.trim()}`);

export const WebsiteStep: React.FC<{
    website: string;
    setWebsite: (v: string) => void;
    busy: boolean;
    error: string | null;
    onBack: () => void;
    onNext: () => void;
}> = ({ website, setWebsite, busy, error, onBack, onNext }) => {
    const { t } = useTranslation();
    const [invalid, setInvalid] = useState(false);
    const submit = () => {
        if (!looksLikeUrl(website)) {
            setInvalid(true);
            return;
        }
        setInvalid(false);
        onNext();
    };
    return (
        <div className="grid items-center gap-12 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
            <form
                className="max-w-[440px]"
                onSubmit={(e) => {
                    e.preventDefault();
                    submit();
                }}
            >
                <StepTitle eyebrow={t('ONBOARDING_V2.WEBSITE.EYEBROW')} title={t('ONBOARDING_V2.WEBSITE.TITLE')} subtitle={t('ONBOARDING_V2.WEBSITE.SUBTITLE')} />
                <div className="mt-7">
                    <Label htmlFor="journey-website" className="mb-1.5 text-xs text-muted-foreground">
                        {t('ONBOARDING.WEBSITE_LABEL')}
                    </Label>
                    <Input
                        id="journey-website"
                        autoFocus
                        value={website}
                        onChange={(e) => {
                            setWebsite(e.target.value);
                            if (invalid) setInvalid(false);
                        }}
                        placeholder={t('ONBOARDING.WEBSITE_PLACEHOLDER') as string}
                        aria-invalid={invalid || undefined}
                        inputMode="url"
                        autoComplete="url"
                    />
                    {invalid && <p className="mt-1.5 text-xs text-destructive">{t('ONBOARDING.WEBSITE_INVALID')}</p>}
                    {error && <p className="mt-1.5 text-xs text-destructive">{error}</p>}
                    <p className="mt-3 text-xs leading-relaxed text-muted-foreground">{t('ONBOARDING_V2.WEBSITE.PRIVACY')}</p>
                </div>
                <StepActions onBack={onBack} onNext={submit} nextLabel={t('ONBOARDING_V2.WEBSITE.START')} busy={busy} testId="journey-website-next" />
            </form>
            <motion.div className="hidden md:block" initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}>
                <BrowserFrame url={website} />
            </motion.div>
        </div>
    );
};

/* ─────────────────────────────── learning ─────────────────────────────── */

/**
 * The reading, narrated. The notebook beside it fills in; this column says
 * what is being done. The vendor can move on as soon as the first things land
 * — the reading does not wait for them and they do not wait for it.
 */
export const LearningStep: React.FC<{ notebook: Notebook; onNext: () => void }> = ({ notebook, onNext }) => {
    const { t } = useTranslation();
    const domain = (notebook.website || '').replace(/^https?:\/\//, '').replace(/\/.*$/, '');
    const [ready, setReady] = useState(false);
    useEffect(() => {
        const id = window.setTimeout(() => setReady(true), 3500);
        return () => window.clearTimeout(id);
    }, []);
    const canGo = ready || notebook.log.length >= 3;

    return (
        <div className="max-w-[460px]">
            <StepTitle eyebrow={t('ONBOARDING_V2.LEARNING.EYEBROW')} title={t('ONBOARDING_V2.LEARNING.TITLE', { domain })} subtitle={t('ONBOARDING_V2.LEARNING.SUBTITLE')} />
            <ol className="mt-7 flex flex-col gap-2.5" aria-live="polite">
                <AnimatePresence initial={false}>
                    {notebook.log.map((line) => (
                        <motion.li
                            key={line.id}
                            layout
                            className="flex items-start gap-2.5 text-sm"
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.35 }}
                        >
                            <span className="mt-0.5 flex size-4 shrink-0 items-center justify-center">
                                {line.status === 'running' && <Spinner className="size-3.5" />}
                                {line.status === 'done' && (
                                    <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} className="flex size-3.5 items-center justify-center rounded-full bg-foreground">
                                        <Check className="size-2.5 text-background" strokeWidth={3} />
                                    </motion.span>
                                )}
                                {line.status === 'failed' && <span className="size-2 rounded-full bg-muted-foreground/40" />}
                            </span>
                            <span className={cn(line.status === 'running' ? 'shimmer' : line.status === 'failed' ? 'text-muted-foreground' : 'text-foreground')}>
                                <LogText line={line} />
                            </span>
                        </motion.li>
                    ))}
                </AnimatePresence>
                {notebook.log.length === 0 && (
                    <li className="flex items-center gap-2.5 text-sm">
                        <Spinner className="size-3.5" />
                        <span className="shimmer">{t('ONBOARDING_V2.NOTEBOOK.STARTING')}</span>
                    </li>
                )}
            </ol>
            <p className="mt-6 text-xs leading-relaxed text-muted-foreground">{t('ONBOARDING_V2.LEARNING.MEANWHILE')}</p>
            <StepActions onNext={onNext} nextLabel={t('ONBOARDING_V2.LEARNING.NEXT')} nextDisabled={!canGo} testId="journey-learning-next" />
        </div>
    );
};

/* ─────────────────────────────── socials ─────────────────────────────── */

export const SocialsStep: React.FC<{
    notebook: Notebook;
    values: Partial<Record<SocialPlatform, string>>;
    setValue: (p: SocialPlatform, v: string) => void;
    meta: { configured: boolean; connected: string[]; connecting: boolean; connect: () => void };
    busy: boolean;
    onBack: () => void;
    onNext: () => void;
}> = ({ notebook, values, setValue, meta, busy, onBack, onNext }) => {
    const { t } = useTranslation();
    const found = SOCIAL_PLATFORMS.filter((p) => notebook.socials[p]);
    // Found ones first, then the rest, so the list reads "these are yours; anything else?".
    const order = [...found, ...SOCIAL_PLATFORMS.filter((p) => !found.includes(p))];
    const connected = meta.connected.length > 0;

    return (
        <div className="max-w-[480px]">
            <StepTitle
                eyebrow={t('ONBOARDING_V2.SOCIALS.EYEBROW')}
                title={t('ONBOARDING_V2.SOCIALS.TITLE')}
                subtitle={found.length ? t('ONBOARDING_V2.SOCIALS.SUBTITLE_FOUND', { count: found.length }) : t('ONBOARDING_V2.SOCIALS.SUBTITLE')}
            />
            <div className="mt-6 flex flex-col gap-2">
                {order.map((platform) => {
                    const Icon = SOCIAL_ICON[platform];
                    const value = values[platform] ?? '';
                    const fromSite = notebook.socials[platform] && cleanHandle(value) === notebook.socials[platform]?.handle;
                    return (
                        <motion.label
                            key={platform}
                            layout
                            htmlFor={`social-${platform}`}
                            className="flex items-center gap-3 rounded-xl border bg-card px-3 py-2 focus-within:ring-2 focus-within:ring-ring/40"
                        >
                            <Icon className="size-4 shrink-0 text-muted-foreground" />
                            <span className="w-20 shrink-0 text-sm">{t(`ONBOARDING_V2.SOCIALS.${platform.toUpperCase()}`)}</span>
                            <span className="text-sm text-muted-foreground">@</span>
                            <input
                                id={`social-${platform}`}
                                value={value}
                                onChange={(e) => setValue(platform, e.target.value)}
                                placeholder={t('ONBOARDING_V2.SOCIALS.PLACEHOLDER') as string}
                                className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground/60"
                                autoComplete="off"
                            />
                            {fromSite && (
                                <span className="inline-flex shrink-0 items-center gap-1 text-[11px] text-muted-foreground">
                                    <Sparkles className="size-3" />
                                    {t('ONBOARDING_V2.SOCIALS.FOUND')}
                                </span>
                            )}
                        </motion.label>
                    );
                })}
            </div>

            {/* Reading their posts needs a connection, not a handle — offered, never required. */}
            {meta.configured && (
                <div className="mt-4 flex items-start gap-3 rounded-xl border border-dashed p-4">
                    <Plug className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                    <div className="min-w-0 flex-1">
                        <p className="text-sm">{connected ? t('ONBOARDING_V2.SOCIALS.META_CONNECTED', { names: meta.connected.join(', ') }) : t('ONBOARDING_V2.SOCIALS.META_TITLE')}</p>
                        {!connected && <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{t('ONBOARDING_V2.SOCIALS.META_BODY')}</p>}
                    </div>
                    {connected ? (
                        <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-success" />
                    ) : (
                        <Button type="button" variant="outline" size="sm" onClick={meta.connect} disabled={meta.connecting}>
                            {meta.connecting && <Loader2 className="animate-spin" data-icon="inline-start" />}
                            {t('ONBOARDING_V2.SOCIALS.META_CONNECT')}
                        </Button>
                    )}
                </div>
            )}

            <StepActions onBack={onBack} onNext={onNext} nextLabel={t('ONBOARDING_V2.CONTINUE')} busy={busy} testId="journey-socials-next" />
        </div>
    );
};

/* ──────────────────────────────── store ──────────────────────────────── */

export const StoreStep: React.FC<{
    notebook: Notebook;
    connected: ShopConnection | null;
    onConnected: (shop: ShopConnection) => void;
    onBack: () => void;
    onNext: () => void;
}> = ({ notebook, connected, onConnected, onBack, onNext }) => {
    const { t } = useTranslation();
    const commerce = useCommerceApi();
    const shop = notebook.shop;
    const label = platformLabel(shop?.platform);
    const site = (notebook.website || '').replace(/^https?:\/\//, '').replace(/\/.*$/, '');
    const [domain, setDomain] = useState(site);
    const [token, setToken] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (!domain && site) setDomain(site);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [site]);

    const connect = async () => {
        if (!shop?.platform) return;
        setSaving(true);
        setError(null);
        try {
            const result = await commerce.connect({ platform: shop.platform as any, shopDomain: domain.trim(), credentials: { accessToken: token.trim() }, grants: ['catalog', 'sales', 'stock'] });
            onConnected(result);
        } catch (e: any) {
            setError(e?.response?.data?.message ?? e?.message ?? t('SALES.SHOP.FAILED'));
        } finally {
            setSaving(false);
        }
    };

    const detecting = !shop;
    return (
        <div className="max-w-[480px]">
            <StepTitle eyebrow={t('ONBOARDING_V2.STORE.EYEBROW')} title={t('ONBOARDING_V2.STORE.TITLE')} subtitle={t('ONBOARDING_V2.STORE.SUBTITLE')} />

            <div className="mt-6">
                <AnimatePresence mode="wait" initial={false}>
                    {connected ? (
                        <motion.div key="connected" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex items-start gap-3 rounded-xl border bg-card p-4" data-testid="journey-store-connected">
                            <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-success" />
                            <div>
                                <p className="text-sm font-medium">{t('ONBOARDING_V2.STORE.CONNECTED', { shop: connected.shopName || connected.shopDomain })}</p>
                                <p className="mt-0.5 text-xs text-muted-foreground">{t('ONBOARDING_V2.STORE.IMPORTING')}</p>
                            </div>
                        </motion.div>
                    ) : detecting ? (
                        <motion.div key="detecting" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center gap-2.5 text-sm">
                            <Spinner className="size-3.5" />
                            <span className="shimmer">{t('ONBOARDING_V2.STORE.DETECTING')}</span>
                        </motion.div>
                    ) : shop.platform ? (
                        <motion.div key="found" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col gap-3">
                            <div className="flex items-start gap-3 rounded-xl border bg-card p-4">
                                <Store className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                                <div className="min-w-0 flex-1">
                                    <p className="text-sm font-medium">
                                        {t(shop.confidence === 'certain' ? 'ONBOARDING_V2.STORE.FOUND' : 'ONBOARDING_V2.STORE.MAYBE', { platform: label })}
                                    </p>
                                    <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
                                        {shop.connectable ? t('ONBOARDING_V2.STORE.WHY') : t('ONBOARDING_V2.STORE.NOT_SUPPORTED', { platform: label })}
                                    </p>
                                </div>
                            </div>
                            {shop.connectable && !open && (
                                <div>
                                    <Button variant="outline" onClick={() => setOpen(true)} data-testid="journey-store-open">
                                        <Plug data-icon="inline-start" />
                                        {t('ONBOARDING_V2.STORE.CONNECT', { platform: label })}
                                    </Button>
                                </div>
                            )}
                            {shop.connectable && open && (
                                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="flex flex-col gap-3 overflow-hidden rounded-xl border bg-card p-4">
                                    <p className="text-xs leading-relaxed text-muted-foreground">{t(`ONBOARDING_V2.STORE.HOWTO.${String(shop.platform).toUpperCase()}`, t('ONBOARDING_V2.STORE.HOWTO.DEFAULT', { platform: label }))}</p>
                                    <div>
                                        <Label htmlFor="journey-shop-domain" className="mb-1.5 text-xs text-muted-foreground">{t('SALES.SHOP.DOMAIN')}</Label>
                                        <Input id="journey-shop-domain" value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="acme.myshopify.com" />
                                    </div>
                                    <div>
                                        <Label htmlFor="journey-shop-token" className="mb-1.5 text-xs text-muted-foreground">{t('SALES.SHOP.TOKEN')}</Label>
                                        <Input id="journey-shop-token" type="password" value={token} onChange={(e) => setToken(e.target.value)} autoComplete="off" />
                                    </div>
                                    {error && (
                                        <p className="flex items-start gap-2 text-xs text-destructive">
                                            <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
                                            {error}
                                        </p>
                                    )}
                                    <div>
                                        <Button variant="outline" onClick={connect} disabled={saving || !domain.trim() || !token.trim()}>
                                            {saving ? <Loader2 className="animate-spin" data-icon="inline-start" /> : <CheckCircle2 data-icon="inline-start" />}
                                            {t('SALES.SHOP.CONNECT')}
                                        </Button>
                                    </div>
                                </motion.div>
                            )}
                        </motion.div>
                    ) : (
                        <motion.div key="none" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex items-start gap-3 rounded-xl border border-dashed p-4">
                            <Store className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                            <p className="text-sm leading-relaxed text-muted-foreground">{t('ONBOARDING_V2.STORE.NOT_DETECTED')}</p>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            <StepActions
                onBack={onBack}
                onNext={onNext}
                nextLabel={connected ? t('ONBOARDING_V2.CONTINUE') : t('ONBOARDING_V2.STORE.LATER')}
                testId="journey-store-next"
            />
        </div>
    );
};

/* ──────────────────────────────── goals ──────────────────────────────── */

export const GoalsStep: React.FC<{
    goals: Goal[];
    setGoals: (g: Goal[]) => void;
    suggested: Goal[];
    onBack: () => void;
    onNext: () => void;
}> = ({ goals, setGoals, suggested, onBack, onNext }) => {
    const { t } = useTranslation();
    return (
        <div className="max-w-[560px]">
            <StepTitle eyebrow={t('ONBOARDING_V2.GOALS.EYEBROW')} title={t('ONBOARDING_V2.GOALS.TITLE')} subtitle={t('ONBOARDING_V2.GOALS.SUBTITLE')} />
            <div className="mt-6 grid gap-2.5 sm:grid-cols-2" role="group" aria-label={t('ONBOARDING_V2.GOALS.TITLE') as string}>
                {GOALS.map((goal, i) => {
                    const ws = workspaceById(goal);
                    const rank = goals.indexOf(goal);
                    const on = rank >= 0;
                    return (
                        <motion.button
                            key={goal}
                            type="button"
                            aria-pressed={on}
                            onClick={() => setGoals(toggleGoal(goals, goal))}
                            className={cn(
                                'relative flex items-start gap-3 rounded-xl border bg-card p-3.5 text-start transition-[box-shadow,border-color] hover:border-foreground/30 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
                                on && 'border-foreground shadow-[0_0_0_1px_var(--foreground)]',
                            )}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.06 }}
                            whileTap={{ scale: 0.985 }}
                            data-testid={`journey-goal-${goal}`}
                        >
                            <WorkspaceSwatch hue={ws.hue} className="mt-0.5 size-9 rounded-lg" />
                            <span className="min-w-0 flex-1">
                                <span className="block text-sm font-medium">{t(ws.labelKey, ws.label)}</span>
                                <span className="mt-0.5 block text-xs leading-relaxed text-muted-foreground">{t(`ONBOARDING_V2.GOALS.${goal.toUpperCase()}`)}</span>
                            </span>
                            {/* The corner says either what Ardi would pick, or where the vendor ranked it. */}
                            {!on && suggested.includes(goal) && (
                                <span className="inline-flex shrink-0 items-center gap-1 rounded-full border px-1.5 py-0.5 text-[11px] text-muted-foreground" title={t('ONBOARDING_V2.GOALS.SUGGESTED') as string}>
                                    <Sparkles className="size-3" />
                                    {t('ONBOARDING_V2.GOALS.SUGGESTED_SHORT')}
                                </span>
                            )}
                            <AnimatePresence>
                                {on && (
                                    <motion.span
                                        className="flex size-5 shrink-0 items-center justify-center rounded-full bg-foreground text-[11px] font-medium text-background tabular-nums"
                                        initial={{ scale: 0 }}
                                        animate={{ scale: 1 }}
                                        exit={{ scale: 0 }}
                                        transition={{ type: 'spring', stiffness: 500, damping: 26 }}
                                    >
                                        {rank + 1}
                                    </motion.span>
                                )}
                            </AnimatePresence>
                        </motion.button>
                    );
                })}
            </div>
            {goals.length === 0 && suggested.length > 0 && (
                <button type="button" onClick={() => setGoals(suggested)} className="mt-3 inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground">
                    <Sparkles className="size-3.5" />
                    {t('ONBOARDING_V2.GOALS.USE_SUGGESTION')}
                </button>
            )}
            <StepActions onBack={onBack} onNext={onNext} nextLabel={t('ONBOARDING_V2.GOALS.NEXT')} nextDisabled={goals.length === 0} testId="journey-goals-next" />
        </div>
    );
};

/* ─────────────────────────────── preparing ─────────────────────────────── */

/**
 * The work being set up, one line at a time, then the door to the dashboard.
 * The lines tick at a reading pace — each is a real thing that now exists —
 * and the button appears when the saving underneath has actually finished.
 */
export const PreparingStep: React.FC<{
    name?: string;
    brandName?: string;
    items: PrepItem[];
    saved: boolean;
    error: string | null;
    onRetry: () => void;
    onFinish: () => void;
}> = ({ name, brandName, items, saved, error, onRetry, onFinish }) => {
    const { t } = useTranslation();
    const [ticked, setTicked] = useState(0);
    const timer = useRef<number>();
    useEffect(() => {
        if (ticked >= items.length) return undefined;
        timer.current = window.setTimeout(() => setTicked((n) => n + 1), ticked === 0 ? 500 : 650);
        return () => window.clearTimeout(timer.current);
    }, [ticked, items.length]);
    const done = ticked >= items.length && saved;

    const label = useMemo(
        () => (item: PrepItem) => {
            const params: Record<string, string | number> = { ...(item.params || {}) };
            for (const [k, v] of Object.entries(params)) if (typeof v === 'number') params[`${k}Formatted`] = new Intl.NumberFormat().format(v);
            return t(`ONBOARDING_V2.PREP.${item.key}`, { ...params, count: typeof item.params?.count === 'number' ? item.params.count : undefined });
        },
        [t],
    );

    return (
        <div className="mx-auto flex max-w-[520px] flex-col items-center text-center">
            <ArdiOrb size={64} state={done ? 'done' : 'thinking'} />
            <AnimatePresence mode="wait">
                <motion.div key={done ? 'done' : 'working'} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }}>
                    <h1 className="mt-7 font-heading text-[28px] leading-tight font-medium tracking-tight text-balance">
                        {done ? (name ? t('ONBOARDING_V2.PREPARING.DONE_TITLE', { name }) : t('ONBOARDING_V2.PREPARING.DONE_TITLE_NO_NAME')) : t('ONBOARDING_V2.PREPARING.TITLE')}
                    </h1>
                    <p className="mt-2.5 text-sm leading-relaxed text-muted-foreground">
                        {done ? t('ONBOARDING_V2.PREPARING.DONE_SUBTITLE', { brand: brandName || t('ONBOARDING_V2.PREPARING.YOUR_BUSINESS') }) : t('ONBOARDING_V2.PREPARING.SUBTITLE')}
                    </p>
                </motion.div>
            </AnimatePresence>

            <ul className="mt-8 flex w-full flex-col gap-1 rounded-2xl border bg-card p-2 text-start" data-testid="journey-prep">
                {items.map((item, i) => {
                    const state = i < ticked ? 'done' : i === ticked ? 'running' : 'waiting';
                    const ws = item.workspace === 'brand' || item.workspace === 'home' ? null : workspaceById(item.workspace);
                    return (
                        <motion.li
                            key={item.id}
                            className="flex items-center gap-3 rounded-xl px-3 py-2.5"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: state === 'waiting' ? 0.35 : 1 }}
                            transition={{ duration: 0.3 }}
                        >
                            {ws ? (
                                <WorkspaceSwatch hue={ws.hue} className="size-7 rounded-md" />
                            ) : (
                                <span className="flex size-7 items-center justify-center rounded-md border bg-background">
                                    <Sparkles className="size-3.5 text-muted-foreground" />
                                </span>
                            )}
                            <span className="min-w-0 flex-1 text-sm">{label(item)}</span>
                            <span className="flex size-4 shrink-0 items-center justify-center">
                                {state === 'running' && <Spinner className="size-3.5" />}
                                {state === 'done' && (
                                    <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} className="flex size-4 items-center justify-center rounded-full bg-foreground">
                                        <Check className="size-2.5 text-background" strokeWidth={3} />
                                    </motion.span>
                                )}
                            </span>
                        </motion.li>
                    );
                })}
            </ul>

            {error && (
                <div className="mt-4 flex items-center gap-2 text-sm text-destructive">
                    <TriangleAlert className="size-4" />
                    {error}
                    <Button variant="outline" size="sm" onClick={onRetry}>
                        {t('ONBOARDING_V2.PREPARING.RETRY')}
                    </Button>
                </div>
            )}

            <AnimatePresence>
                {done && (
                    <motion.div className="mt-8" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                        <Button variant="outline" size="lg" onClick={onFinish} autoFocus data-testid="journey-finish">
                            {t('ONBOARDING_V2.PREPARING.OPEN')}
                            <ArrowRight className="rtl:rotate-180" data-icon="inline-end" />
                        </Button>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};
